import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrandMark } from './screens';

const Back = ({ onClick }) => (
  <button type="button" className="lgl-m-back" onClick={onClick} aria-label="Back">←</button>
);

const statusClass = (value) => String(value || 'not set')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-');

const StatusBadge = ({ value }) => (
  <span className={`lgl-m-status lgl-m-statusbadge is-${statusClass(value)}`}>
    {value || 'Not set'}
  </span>
);

export const ModuleListScreen = ({
  project,
  module,
  rows,
  loading,
  error,
  onBack,
  onOpen,
  onRefresh,
}) => {
  const [query, setQuery] = useState('');
  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => module.searchFields.some((field) => (
      String(row[field] || '').toLowerCase().includes(needle)
    )));
  }, [module.searchFields, query, rows]);

  return (
    <>
      <header className="lgl-m-top">
        <div className="lgl-m-navrow">
          <Back onClick={onBack} />
          <div>
            <p className="lgl-m-kicker">{project?.job_number}</p>
            <h1 className="lgl-m-title">{module.label}</h1>
            <p className="lgl-m-sub">{visibleRows.length} of {rows.length} records</p>
          </div>
          <BrandMark />
        </div>
        <input
          className="lgl-m-search"
          type="search"
          inputMode="search"
          placeholder={`Search ${module.label.toLowerCase()}`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </header>
      <div className="lgl-m-body">
        {error ? <div className="lgl-m-err">{error}</div> : null}
        {loading ? <p className="lgl-m-empty">Loading {module.label.toLowerCase()}…</p> : null}
        {!loading && visibleRows.map((row) => (
          <button
            key={row.id}
            type="button"
            className="lgl-m-card lgl-m-module-card"
            style={{ '--lgl-module-accent': module.accent }}
            onClick={() => onOpen(row.id)}
          >
            <div className="lgl-m-linehead">
              <div>
                <div className="lgl-m-jobname">{module.listTitle(row)}</div>
                <div className="lgl-m-sub">{module.listSubtitle(row) || 'No details yet'}</div>
              </div>
              {module.statusField ? <StatusBadge value={row[module.statusField]} /> : null}
            </div>
          </button>
        ))}
        {!loading && !rows.length ? (
          <p className="lgl-m-empty">No {module.label.toLowerCase()} records on this job yet.</p>
        ) : null}
        {!loading && rows.length && !visibleRows.length ? (
          <p className="lgl-m-empty">Nothing matches that search.</p>
        ) : null}
        {onRefresh ? (
          <button type="button" className="lgl-m-linkbtn" onClick={onRefresh}>Refresh list</button>
        ) : null}
      </div>
    </>
  );
};

const dateValue = (value) => String(value || '').slice(0, 10);

const ModuleField = ({ field, value, onChange }) => {
  if (field.type === 'chips' || field.type === 'select') {
    return (
      <section className="lgl-m-field">
        <div className="lgl-m-stephead"><strong>{field.label}</strong></div>
        <div className="lgl-m-chips">
          {field.options.map((option) => (
            <button
              key={option || 'empty'}
              type="button"
              className={`lgl-m-chip${String(value || '') === String(option) ? ' is-on' : ''}`}
              disabled={field.readOnly}
              onClick={() => onChange(option)}
            >
              {option || 'Clear'}
            </button>
          ))}
        </div>
      </section>
    );
  }

  if (field.type === 'textarea') {
    return (
      <section className="lgl-m-field">
        <label htmlFor={`module-field-${field.key}`}>{field.label}</label>
        <textarea
          id={`module-field-${field.key}`}
          className="lgl-m-comment"
          value={value || ''}
          readOnly={field.readOnly}
          onChange={(event) => onChange(event.target.value)}
        />
      </section>
    );
  }

  return (
    <section className="lgl-m-field">
      <label htmlFor={`module-field-${field.key}`}>{field.label}</label>
      <input
        id={`module-field-${field.key}`}
        type={field.type === 'number' ? 'number' : field.type}
        inputMode={field.type === 'number' ? 'decimal' : undefined}
        step={field.type === 'number' ? 'any' : undefined}
        value={field.type === 'date' ? dateValue(value) : (value || '')}
        readOnly={field.readOnly}
        onChange={(event) => onChange(event.target.value)}
      />
    </section>
  );
};

export const ModuleDetailScreen = ({
  project,
  module,
  row,
  neighbors,
  saveState,
  onBack,
  onNeighbor,
  onSaveField,
}) => {
  const [draft, setDraft] = useState(() => row || {});
  const [dirtyKeys, setDirtyKeys] = useState(new Set());
  const loadedRowId = useRef(row?.id);

  useEffect(() => {
    if (String(loadedRowId.current) === String(row?.id)) return;
    loadedRowId.current = row?.id;
    setDraft(row || {});
    setDirtyKeys(new Set());
  }, [row]);

  if (!row) {
    return (
      <div className="lgl-m-body">
        <p className="lgl-m-empty">This record is no longer in the tracker.</p>
        <button type="button" className="lgl-m-card" onClick={onBack}>Back to list</button>
      </div>
    );
  }

  const updateField = (field, value) => {
    if (field.readOnly) return;
    setDraft((current) => ({ ...current, [field.key]: value }));
    setDirtyKeys((current) => new Set([...current, field.key]));
  };

  const save = async () => {
    const keys = module.fields
      .filter((field) => !field.readOnly && dirtyKeys.has(field.key))
      .map((field) => field.key);
    for (const key of keys) {
      const saved = await onSaveField({ ...row, ...draft }, key, draft[key]);
      if (!saved) return;
    }
    setDirtyKeys(new Set());
  };

  return (
    <>
      <header className="lgl-m-top">
        <div className="lgl-m-navrow">
          <Back onClick={onBack} />
          <div>
            <p className="lgl-m-kicker">{project?.job_number} · {module.label}</p>
            <h1 className="lgl-m-title">{module.listTitle(row)}</h1>
            <p className="lgl-m-sub">{module.listSubtitle(row)}</p>
          </div>
          <BrandMark />
        </div>
        <p className={`lgl-m-status${saveState.status === 'saved' ? ' is-ok' : ''}${saveState.status === 'error' ? ' is-err' : ''}`}>
          {saveState.message || (dirtyKeys.size ? 'Changes are ready to save.' : 'Edit the record, then save.')}
        </p>
      </header>
      <div className="lgl-m-body">
        {module.fields.map((field) => (
          <ModuleField
            key={field.key}
            field={field}
            value={draft[field.key]}
            onChange={(value) => updateField(field, value)}
          />
        ))}
        <button
          type="button"
          className="lgl-m-btn"
          disabled={!dirtyKeys.size || saveState.status === 'saving'}
          onClick={save}
        >
          {saveState.status === 'saving' ? 'Saving…' : 'Save changes'}
        </button>
        <div className="lgl-m-pager">
          <button type="button" disabled={!neighbors.prev} onClick={() => neighbors.prev && onNeighbor(neighbors.prev)}>
            Previous
          </button>
          <button type="button" disabled={!neighbors.next} onClick={() => neighbors.next && onNeighbor(neighbors.next)}>
            Next record
          </button>
        </div>
      </div>
    </>
  );
};

